import React, { useState } from 'react';
import clientCandidateDataService from '../../../services/clientCandidateDataService';

/**
 * PreferencesWidget
 * Remote toggles, Relocation with cities, Do Not Contact
 */

const PreferencesWidget = ({ profile, candidateId, onRefresh }) => {
  const [isAddingCity, setIsAddingCity] = useState(false);
  const [editingCityIndex, setEditingCityIndex] = useState(null);
  const [cityForm, setCityForm] = useState('');

  const clientData = profile?.client_data || {};
  const preferences = {
    open_to_remote: clientData.open_to_remote || false,
    remote_only: clientData.remote_only || false,
    open_to_relocation: clientData.open_to_relocation || false,
    relocation_cities: clientData.relocation_cities || [],
    do_not_contact: clientData.do_not_contact || false
  };

  const saveData = async (updates) => {
    try {
      // Clean status is no longer reset here. Marking a profile unclean is
      // reserved for tag changes only — editing preferences is routine data
      // upkeep and shouldn't silently strip a reviewed profile's clean flag.
      await clientCandidateDataService.updateData(candidateId, { ...clientData, ...updates });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save');
    }
  };

  const handleToggle = async (field) => {
    const newValue = !preferences[field];
    const updates = { [field]: newValue };
    
    if (field === 'remote_only' && newValue) updates.open_to_remote = true;
    if (field === 'open_to_remote' && !newValue) updates.remote_only = false;
    if (field === 'open_to_relocation' && !newValue) updates.relocation_cities = [];
    
    await saveData(updates);
  };

  const handleAddCity = () => { setCityForm(''); setIsAddingCity(true); setEditingCityIndex(null); };
  
  const handleEditCity = (index) => { setCityForm(preferences.relocation_cities[index]); setEditingCityIndex(index); setIsAddingCity(false); };

  const handleSaveCity = async () => {
    if (!cityForm.trim()) { alert('Please enter a city'); return; }
    let newCities = [...preferences.relocation_cities];
    if (editingCityIndex !== null) newCities[editingCityIndex] = cityForm.trim();
    else newCities.push(cityForm.trim());
    await saveData({ relocation_cities: newCities });
    setIsAddingCity(false); setEditingCityIndex(null); setCityForm('');
  };

  const handleDeleteCity = async (index) => {
    await saveData({ relocation_cities: preferences.relocation_cities.filter((_, i) => i !== index) });
  };

  const handleCancelCity = () => { setIsAddingCity(false); setEditingCityIndex(null); setCityForm(''); };

  const Toggle = ({ field, label, description, disabled = false }) => {
    const isOn = preferences[field];
    return (
      <div className={`flex items-start justify-between py-3 ${disabled ? 'opacity-50' : ''}`}>
        <div className="flex-1 min-w-0 mr-3">
          <h4 className="text-sm font-medium text-gray-800">{label}</h4>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
        <button
          onClick={() => !disabled && handleToggle(field)}
          disabled={disabled}
          className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          style={{ backgroundColor: isOn ? (field === 'do_not_contact' ? '#dc2626' : '#2563eb') : '#e5e7eb' }}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isOn ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
    );
  };

  return (
    <div className="divide-y divide-gray-100">
      {/* Remote */}
      <div className="pb-2">
        <Toggle field="open_to_remote" label="Open to Remote" description="Willing to work remotely or hybrid" />
        <Toggle field="remote_only" label="Remote Only" description="Only interested in fully remote positions" disabled={!preferences.open_to_remote} />
      </div>

      {/* Relocation */}
      <div className="py-2">
        <Toggle field="open_to_relocation" label="Open to Relocation" description="Willing to relocate for the right opportunity" />

        {preferences.open_to_relocation && (
          <div className="mt-2 ml-4 pl-4 border-l-2 border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-600">Preferred Cities</label>
              {!isAddingCity && editingCityIndex === null && (
                <button onClick={handleAddCity} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add City
                </button>
              )}
            </div>

            {(isAddingCity || editingCityIndex !== null) && (
              <div className="flex gap-2 mb-2">
                <input type="text" value={cityForm} onChange={(e) => setCityForm(e.target.value)} className="flex-1 px-2 py-1 text-sm border rounded" placeholder="e.g., New York, NY" autoFocus />
                <button onClick={handleSaveCity} className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">{editingCityIndex !== null ? 'Update' : 'Add'}</button>
                <button onClick={handleCancelCity} className="px-2 py-1 border text-xs rounded hover:bg-gray-50">Cancel</button>
              </div>
            )}

            {preferences.relocation_cities.length === 0 && !isAddingCity ? (
              <p className="text-xs text-gray-400 italic">No cities specified</p>
            ) : (
              <div className="space-y-1">
                {preferences.relocation_cities.map((city, index) => (
                  <div key={index} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded group">
                    <div className="flex items-center gap-2">
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span className="text-sm text-gray-700">{city}</span>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                      <button onClick={() => handleEditCity(index)} className="p-0.5 text-gray-400 hover:text-emerald-700">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button onClick={() => handleDeleteCity(index)} className="p-0.5 text-gray-400 hover:text-red-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Do Not Contact */}
      <div className="pt-2">
        <div className={`rounded-lg p-3 ${preferences.do_not_contact ? 'bg-red-50 border border-red-200' : ''}`}>
          <Toggle field="do_not_contact" label="Do Not Contact" description="Flag this candidate to prevent outreach" />
          {preferences.do_not_contact && (
            <div className="mt-2 flex items-center gap-2 text-red-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-xs font-medium">This candidate should not be contacted</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreferencesWidget;
